import React, { useCallback, useState } from 'react';
import { LoginForm } from '../auth/LoginForm';
import { ForgotPasswordForm } from '../auth/ForgotPasswordForm';
import { useAppContext } from '@/hooks/useAppContext';
import { Modal, ModalHeader, ModalBody } from '@/components/common';
import type { LoginCredentials } from '@/types/auth.types';
import { FaEnvelopeOpenText, FaExclamationTriangle } from 'react-icons/fa';

interface AuthModalProps {
  onSuccess?: () => void;
  /** Open on the "Forgot password?" form, e.g. from a dead reset link. */
  initialMode?: AuthMode;
}

type AuthMode = 'login' | 'forgot';

/**
 * Sign-in only. Minka is invite-only (docs/PLATFORM_ROLES_AND_ORGANIZATIONS.md
 * §3.1 invariant 4): accounts exist because someone was invited, so there is no
 * Register tab to switch to — the registration form lives on the invite landing
 * page, where the invited address is known.
 *
 * "Forgot password?" swaps the sign-in form for the reset request in place.
 */
export const AuthModal: React.FC<AuthModalProps> = ({ onSuccess, initialMode = 'login' }) => {
  const { auth, notice } = useAppContext();
  const { login, isLoading, error, clearError } = auth;
  const [mode, setMode] = useState<AuthMode>(initialMode);

  const handleLogin = useCallback(async (credentials: LoginCredentials) => {
    try {
      await login(credentials);
      // Whatever ended the last session has been read and acted on; carrying
      // it into the new one would greet the next person with it.
      notice.clearNotice();
      onSuccess?.();
    } catch {
      // Error is handled by the useAuth hook
    }
  }, [login, notice, onSuccess]);

  return (
    <Modal
      isOpen={true}
      onClose={() => {}} // Auth modal cannot be closed - user must authenticate
      size="md"
      showCloseButton={false}
      className="bg-gradient-to-b from-game-dark/95 via-game-dark/90 to-game-medium/30 backdrop-blur-md"
    >
      <ModalHeader showCloseButton={false}>
        <div className="text-center w-full">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/assets/logo/minka-logo-color.png" 
              alt="Minka" 
              className="h-24 w-auto object-contain"
            />
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* A deactivated account is signed out where it stands (§6.5). Without
            this the user would arrive at a sign-in form with no idea why, and
            try the password that is no longer the problem. */}
        {notice.notice && (
          <div className="mb-6 flex items-start gap-3 p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg text-sm">
            <FaExclamationTriangle className="text-game-golden flex-shrink-0 mt-0.5" />
            <span>{notice.notice}</span>
          </div>
        )}

        {mode === 'forgot' ? (
          <ForgotPasswordForm onBack={() => setMode('login')} />
        ) : (
          <LoginForm
            onSubmit={handleLogin}
            isLoading={isLoading}
            error={error}
            onForgotPassword={() => {
              clearError?.();
              setMode('forgot');
            }}
          />
        )}

        <div className="mt-6 flex items-start gap-3 p-3 bg-game-medium/20 border border-game-medium/50 rounded-lg text-sm text-gray-200">
          <FaEnvelopeOpenText className="text-game-golden flex-shrink-0 mt-0.5" />
          <span>Minka is invite-only — ask an island admin for an invitation.</span>
        </div>
      </ModalBody>
    </Modal>
  );
};
